import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutGrid, Construction, FileSearch, ArrowLeftRight, PlusCircle,
  Truck, FilePen, Users, UserPlus, Calculator, CircleDollarSign,
  ShieldCheck, BarChart3, Mail, Settings, Plug, LogOut, TestTube,
} from "lucide-react";

const NAV = [
  { section: "Every day", items: [
    { to: "/dashboard", label: "Dashboard", Icon: LayoutGrid, testid: "nav-dashboard" },
    { to: "/machines", label: "Machines", Icon: Construction, testid: "nav-machines", hideFor: ["Accounts"] },
    { to: "/machine-details", label: "Machine details", Icon: FileSearch, testid: "nav-machine-details", hideFor: ["Accounts"] },
    { to: "/hires", label: "Hires", Icon: ArrowLeftRight, testid: "nav-hires" },
    { to: "/start-a-hire", label: "Start a hire", Icon: PlusCircle, testid: "nav-start-a-hire", hideFor: ["Accounts", "Read-only"] },
    { to: "/transport", label: "Transport", Icon: Truck, testid: "nav-transport", hideFor: ["Accounts"] },
    { to: "/quotes", label: "Quotes", Icon: FilePen, testid: "nav-quotes" },
  ]},
  { section: "Customers & billing", items: [
    { to: "/customers", label: "Customers", Icon: Users, testid: "nav-customers" },
    { to: "/new-customer", label: "New customer setup", Icon: UserPlus, testid: "nav-new-customer" },
    { to: "/month-end", label: "Month-end billing", Icon: Calculator, testid: "nav-month-end" },
    { to: "/invoices-money-owed", label: "Invoices & money owed", Icon: CircleDollarSign, testid: "nav-invoices" },
  ]},
  { section: "Records", items: [
    { to: "/rego-inspections", label: "Rego & inspections", Icon: ShieldCheck, testid: "nav-rego", hideFor: ["Accounts"] },
    { to: "/reports", label: "Reports", Icon: BarChart3, testid: "nav-reports" },
    { to: "/emails", label: "Emails", Icon: Mail, testid: "nav-emails" },
    { to: "/dry-run", label: "Dry run", Icon: TestTube, testid: "nav-dry-run", hideFor: ["Accounts", "Read-only"] },
    { to: "/settings", label: "Settings", Icon: Settings, testid: "nav-settings", hideFor: ["Accounts"] },
    { to: "/connections", label: "Connections", Icon: Plug, testid: "nav-connections", hideFor: ["Accounts", "Office", "Read-only"] },
  ]},
];

function initials(name, email) {
  const src = (name || email || "").trim();
  if (!src) return "?";
  const parts = src.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside className="ach-sidebar" data-testid="app-sidebar">
      <div className="ach-sidebar__logo" data-testid="sidebar-logo">
        <img src="/assets/logo-mark.png" alt="All Cranes" className="ach-sidebar__logo-img" />
        <div className="ach-sidebar__logo-tag">sales · hire · advice</div>
      </div>
      {NAV.map((s) => {
        const items = s.items.filter(it => !(it.hideFor || []).includes(user?.role));
        if (items.length === 0) return null;
        return (
          <React.Fragment key={s.section}>
            <div className="ach-sidebar__section">{s.section}</div>
            <nav className="ach-nav">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to}
                  className={({ isActive }) => "ach-nav__item" + (isActive ? " active" : "")}
                  data-testid={it.testid}>
                  <it.Icon strokeWidth={2} />
                  <span>{it.label}</span>
                </NavLink>
              ))}
            </nav>
          </React.Fragment>
        );
      })}
      <div style={{ flex: 1 }} />

      {user && (
        <div
          className="ach-sidebar__account"
          data-testid="sidebar-account"
          style={{
            margin: "8px 12px 12px",
            padding: "10px 12px",
            borderTop: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              background: "#0B1F3A",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 12,
              flex: "0 0 auto",
            }}
          >
            {initials(user.name, user.email)}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              data-testid="sidebar-account-name"
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ach-text-1, #fff)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={user.name || user.email}
            >
              {user.name || user.email}
            </div>
            <div
              style={{
                fontSize: 11,
                color: "var(--ach-text-2, #b7bcc7)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={user.email}
            >
              {user.role ? `${user.role} · ${user.email}` : user.email}
            </div>
          </div>
          <button
            type="button"
            onClick={onLogout}
            data-testid="logout-button"
            aria-label="Sign out"
            title="Sign out"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "var(--ach-text-2, #b7bcc7)",
              width: 32,
              height: 32,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              flex: "0 0 auto",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(255,255,255,0.06)";
              e.currentTarget.style.color = "#fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--ach-text-2, #b7bcc7)";
            }}
          >
            <LogOut size={16} strokeWidth={2} />
          </button>
        </div>
      )}
    </aside>
  );
}
